import type {dbItem, shareOutput, unshareOutput} from '@/shared/utils/crud-helper/models'

/**
 * Access to a shared folder (decision D6): Owner, Can edit, Can view. Members are stored as
 * `edit` or `view` (`OmicsBoxShares` MEMBER rows); `owner` is the person who shared it.
 */
export type ShareAccess = 'owner' | 'edit' | 'view'

/** The signed-in user's own space, "My Files": `<identityId>/…` in the user-data bucket. */
export interface OwnRoot {
  kind: 'own'
}

/**
 * A shared folder (E1): its files stay at `<ownerIdentityId>/<folderPath>…`, in the owner's space.
 * Browsed through the sharing API (`createShareStore`), never with the caller's own credentials.
 */
export interface ShareRoot {
  kind: 'share'
  /** The share's id (`OmicsBoxShares` `SHARE#<id>`). */
  id: string
  ownerIdentityId: string
  /** Relative to the owner's root, always ending in `/`. */
  folderPath: string
  access: ShareAccess
  /** Shown in the breadcrumbs. Display only: never part of a key or a request. */
  name?: string
}

/** Where a Files browser is pointed. Every path a {@link FileStore} takes is relative to its root. */
export type StorageRoot = OwnRoot | ShareRoot

/** Shared so that a memoised root keeps one identity across renders. */
export const OWN_ROOT: OwnRoot = {kind: 'own'}

/** Progress and cancelling for {@link FileStore.upload}. A store may ignore them (My Files does). */
export interface UploadOptions {
  /** Bytes sent so far, of `total`. */
  onProgress?: (loaded: number, total: number) => void
  signal?: AbortSignal
}

/**
 * Progress of a rename, move or copy that runs in the background (shared folders): objects done, of
 * `total`. My Files reports nothing here; its changes arrive through the notification socket.
 */
export interface OperationOptions {
  onProgress?: (done: number, total: number) => void
}

/** What a multi-item delete did: how many objects went, and the paths that could not be deleted. */
export interface RemoveResult {
  deleted: number
  failed: string[]
}

/**
 * The file operations the Files browser performs, for one {@link StorageRoot}.
 *
 * Paths are relative to the root and use the storage index's form: `a/b.txt` for a file, `a/` for
 * a folder, which is what `CustomFileData.id` and `dbItem.file` hold. A *folder* argument is
 * written without slashes at either end, `''` being the root: `a/b`.
 */
export interface FileStore {
  readonly root: StorageRoot
  /** Every indexed item under `folder`. */
  list(folder: string): Promise<dbItem[] | undefined>
  /** A URL to open, or the file's content when `asBlob` (to zip several files). */
  download(path: string, asBlob: boolean): Promise<string | Blob>
  upload(path: string, file: File, options?: UploadOptions): Promise<unknown>
  /** `path` is the new folder's path without the trailing slash. */
  createFolder(path: string): Promise<unknown>
  remove(path: string): Promise<string>
  /**
   * Deletes several items in one go, where the store can: a folder (ending in `/`) goes with
   * everything in it. Without it, callers delete every key with {@link remove}.
   */
  removeMany?(paths: string[]): Promise<RemoveResult>
  /** `destination` is the item's new path: ends in `/` when the item is a folder. */
  rename(source: string, destination: string, options?: OperationOptions): Promise<unknown>
  move(sources: string[], destinationFolder: string, options?: OperationOptions): Promise<unknown>
  copy(sources: string[], destinationFolder: string, options?: OperationOptions): Promise<unknown>
  /** Public links (Share / Unshare). Owner only (E9), so a shared folder's store leaves them out. */
  createPublicLinks?(paths: string[]): Promise<shareOutput>
  removePublicLinks?(paths: string[]): Promise<unshareOutput>
}
